import { asBoolean, asDate, asNumber, asObject, asRaw, asString } from './guards'
import { GameInfo } from './game-info'
import { User } from './user'

/** Port of constructors/prize.js */
export class Prize {
  id: number | null
  key: string | null
  game_info_id: number | null
  game_info: GameInfo | null
  owner_id: number | null
  owner: User | null
  free: boolean | null
  created_at: Date | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.key = asString(d.key)
    this.game_info_id = asNumber(d.game_info_id)
    this.game_info = asObject(d.game_info) ? new GameInfo(d.game_info) : null
    this.owner_id = asNumber(d.owner_id)
    this.owner = asObject(d.owner) ? new User(d.owner) : null
    this.free = asBoolean(d.free)
    this.created_at = asDate(d.created_at)
  }
}

/**
 * A key awarded to a user.
 *
 * The giveaway is kept to the few fields My Loot renders rather than a full
 * `Giveaway`: the original embedded the whole object graph, which pulled a
 * countdown timer per prize into a page that shows no countdowns.
 */
export class UserPrize {
  id: number | null
  user_id: number | null
  user: User | null
  key: string | null
  giveaway_id: number | null
  giveaway_name: string | null
  giveaway_slug: string | null
  game_info_id: number | null
  game_info: GameInfo | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    const giveaway = asObject(d.giveaway)
    this.id = asNumber(d.id)
    this.user_id = asNumber(d.user_id)
    this.user = asObject(d.user) ? new User(d.user) : null
    this.key = asString(d.key)
    this.giveaway_id = asNumber(d.giveaway_id)
    this.giveaway_name = asString(giveaway?.name)
    this.giveaway_slug = asString(giveaway?.slug)
    // A key won from a giveaway has no game of its own; it is the giveaway's.
    const game = asObject(d.game_info) ?? asObject(giveaway?.game_info)
    this.game_info = game ? new GameInfo(game) : null
    this.game_info_id = asNumber(d.game_info_id) ?? this.game_info?.id ?? null
  }

  /** What the key unlocks, falling back to the giveaway that awarded it. */
  get title(): string {
    return this.game_info?.name ?? this.giveaway_name ?? 'Key'
  }

  /** The public address of the giveaway this came from, if it has one. */
  get giveaway_path(): string | null {
    const slug = this.giveaway_slug ?? (this.giveaway_id !== null ? String(this.giveaway_id) : null)
    return slug === null ? null : `/g/${slug}`
  }
}
